/*
 * In-app browser nudge for verte.co.in (7 Oct 2026).
 *
 * Ported from verte-web's InAppBrowserNudge (vertewatches.com), same wording
 * and the same two escape routes:
 *   - Android: a Chrome intent:// link reopens the page in Chrome. Instagram's
 *     browser sometimes ignores intent links, so if the page is still showing
 *     1.5 s later the card switches to the manual step (⋮ > Open in Chrome).
 *   - iPhone: in-app browsers cannot be escaped from code, so the card gives
 *     the ••• menu step and a copy-link button.
 *
 * What is different from verte-web, decided with the user on 7 Oct:
 *   - Shown on entry, on whatever page the shopper lands (verte-web shows it
 *     on the buy path only), closable, once per session.
 *   - A 50/50 test. Each device is assigned "show" or "control" once, and the
 *     Shopify cart carries the group as the hidden attribute `_iab_test`
 *     (show / control / switched), so orders can be compared by group.
 *   - The cart and the ad parameters travel to the new browser. Without that,
 *     the shopper arrives in Chrome or Safari with an empty cart and no
 *     fbclid/UTMs, and the purchase stops counting for the ad that brought
 *     them, which is what the India ad set optimises on.
 *
 * Never blocks anything: every step is wrapped, and a failure just means no
 * card.
 */
(function () {
  'use strict';

  var S = (window.VerteIAB && window.VerteIAB.strings) || {};
  var CARRY = ['fbclid', 'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id', 'gclid'];
  var DISMISS_KEY = 'verte_iab_nudge_dismissed';
  var GROUP_KEY = 'verte_iab_group';
  var TAGGED_KEY = 'verte_iab_tagged';
  var LANDING_KEY = 'verte_iab_landing';

  function get(store, k) { try { return window[store].getItem(k); } catch (e) { return null; } }
  function set(store, k, v) { try { window[store].setItem(k, v); return true; } catch (e) { return false; } }

  /* Custom customer event. Reaches GA4/Meta only if a custom pixel subscribes
     to it (Settings > Customer events); the cart attribute is the record the
     test relies on. Same event names as vertewatches.com. */
  function publish(name, data) {
    try { if (window.Shopify && Shopify.analytics && Shopify.analytics.publish) Shopify.analytics.publish(name, data); } catch (e) {}
  }

  function tagCart(value) {
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { _iab_test: value } })
    }).catch(function () {});
  }

  var ua = navigator.userAgent || '';
  var app = /Instagram/i.test(ua) ? 'instagram'
    : /\bFBAN\b|\bFBAV\b|FB_IAB/i.test(ua) ? 'facebook' : null;
  var platform = /iPhone|iPad|iPod/i.test(ua) ? 'ios' : /Android/i.test(ua) ? 'android' : 'other';
  var params = new URLSearchParams(location.search);

  /* ── 1. Arrived in a real browser from the card: refill the cart ───────── */
  if (!app && params.has('vrt_iab')) {
    var items = (params.get('vrt_cart') || '').split(',').map(function (p) {
      var bits = p.split(':');
      return { id: Number(bits[0]), quantity: Number(bits[1]) || 1 };
    }).filter(function (i) { return i.id > 0 && i.quantity > 0 && i.quantity < 20; });

    (items.length
      ? fetch('/cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: items }) })
      : Promise.resolve()
    ).catch(function () {}).then(function () { return tagCart('switched'); });
    publish('in_app_browser_arrived', { app: params.get('vrt_iab'), items: items.length });

    // Tidy the address bar; the ad parameters stay for the pixels on this page.
    params.delete('vrt_iab');
    params.delete('vrt_cart');
    var qs = params.toString();
    try { history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash); } catch (e) {}
    return;
  }

  if (!app) return;

  /* ── 2. Inside Instagram or Facebook ─────────────────────────────────── */

  // The landing page's ad parameters, kept for the visit: they drop out of the
  // URL on the next page, and the new browser must still receive them.
  if (!get('sessionStorage', LANDING_KEY)) {
    var landing = {};
    CARRY.forEach(function (k) { if (params.get(k)) landing[k] = params.get(k); });
    set('sessionStorage', LANDING_KEY, JSON.stringify(landing));
  }

  // 50/50, once per device. No storage means no stable group: leave the
  // shopper out of the test and show nothing.
  var group = get('localStorage', GROUP_KEY);
  if (group !== 'show' && group !== 'control') {
    group = Math.random() < 0.5 ? 'show' : 'control';
    if (!set('localStorage', GROUP_KEY, group)) return;
  }
  if (!get('sessionStorage', TAGGED_KEY)) {
    tagCart(group);
    set('sessionStorage', TAGGED_KEY, '1');
    publish('in_app_browser_detected', { app: app, platform: platform, group: group });
  }
  if (group !== 'show' || get('sessionStorage', DISMISS_KEY) === '1') return;

  /** This page, plus the landing ad parameters and the cart, flagged for step 1. */
  function escapeUrl() {
    return fetch('/cart.js').then(function (r) { return r.json(); }).catch(function () { return null; })
      .then(function (cart) {
        var u = new URL(location.href);
        var landing = {};
        try { landing = JSON.parse(get('sessionStorage', LANDING_KEY) || '{}'); } catch (e) {}
        Object.keys(landing).forEach(function (k) { if (!u.searchParams.has(k)) u.searchParams.set(k, landing[k]); });
        u.searchParams.set('vrt_iab', app);
        if (cart && cart.items && cart.items.length) {
          u.searchParams.set('vrt_cart', cart.items.map(function (i) { return i.variant_id + ':' + i.quantity; }).join(','));
        }
        return u;
      });
  }

  /** Put the escape URL in the address bar, so the app's own "open in browser" carries it. */
  function primeAddressBar() {
    return escapeUrl().then(function (u) {
      try { history.replaceState(history.state, '', u.pathname + u.search + u.hash); } catch (e) {}
      return u;
    });
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text) n.textContent = text;
    return n;
  }

  function show() {
    var card = el('div', 'vrt-iab');
    card.setAttribute('role', 'region');
    card.setAttribute('aria-label', S.title || 'Open in browser');
    var body = el('div', 'vrt-iab__body');
    body.appendChild(el('p', 'vrt-iab__title', S.title));
    var text = el('p', 'vrt-iab__text', platform === 'ios' ? S.ios : S.android);
    body.appendChild(text);

    var btn = el('button', 'vrt-iab__btn', platform === 'android' ? S.open_chrome : S.copy_link);
    btn.type = 'button';
    btn.addEventListener('click', function () {
      if (platform === 'android') {
        publish('in_app_browser_escape', { app: app, method: 'chrome_intent', group: group });
        escapeUrl().then(function (u) {
          var fallback = encodeURIComponent(u.href);
          window.location.href = 'intent://' + u.host + u.pathname + u.search +
            '#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=' + fallback + ';end';
          // Still here after 1.5 s: the app ignored the intent. Give the
          // manual route, with the escape URL already in the address bar.
          setTimeout(function () {
            if (document.visibilityState !== 'visible') return;
            primeAddressBar();
            text.textContent = S.android_fallback;
            btn.style.display = 'none';
          }, 1500);
        });
      } else {
        publish('in_app_browser_escape', { app: app, method: 'copy_link', group: group });
        escapeUrl().then(function (u) {
          return navigator.clipboard.writeText(u.href).then(function () {
            btn.textContent = S.copied;
            setTimeout(function () { btn.textContent = S.copy_link; }, 2500);
          });
        }).catch(function () {});
      }
    });
    body.appendChild(btn);
    card.appendChild(body);

    var close = el('button', 'vrt-iab__close', '×');
    close.type = 'button';
    close.setAttribute('aria-label', S.dismiss || 'Dismiss');
    close.addEventListener('click', function () {
      set('sessionStorage', DISMISS_KEY, '1');
      publish('in_app_browser_nudge_dismissed', { app: app, group: group });
      card.remove();
      document.documentElement.removeAttribute('data-iab-nudge');
    });
    card.appendChild(close);
    document.body.appendChild(card);
    document.documentElement.setAttribute('data-iab-nudge', '1');

    // iPhone: the ••• menu opens whatever is in the address bar.
    if (platform === 'ios') primeAddressBar();

    // Step aside while Shopflo's checkout or cart drawer is on screen, without
    // counting as a dismissal. On screen, not just full size: Shopflo keeps a
    // full-size cart drawer frame parked just past the right edge.
    var watch = setInterval(function () {
      if (!document.body.contains(card)) { clearInterval(watch); return; }
      var w = window.innerWidth, h = window.innerHeight;
      var open = Array.prototype.some.call(
        document.querySelectorAll('iframe[src*="checkout.shopflo.co"]'),
        function (f) {
          var r = f.getBoundingClientRect();
          return r.width > 300 && r.left < w - 50 && r.right > 50 && r.top < h - 50 && r.bottom > 50;
        });
      card.style.display = open ? 'none' : '';
      if (open) document.documentElement.removeAttribute('data-iab-nudge');
      else document.documentElement.setAttribute('data-iab-nudge', '1');
    }, 1000);
  }

  // Wait for the India geo gate (layout/theme.liquid) to reveal the page, then
  // give the shopper a moment to see where they landed.
  var tries = 0;
  (function whenRevealed() {
    if (document.getElementById('verte-geo-gate') && tries++ < 40) return setTimeout(whenRevealed, 100);
    setTimeout(function () { try { show(); } catch (e) {} }, 1200);
  })();
})();
